import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { partnersApi } from '@/api/endpoints'
import { Avatar, BackIcon } from '@/components/icons'
import { Button } from '@/components/ui/Button'
import { ErrorState, PageSpinner } from '@/components/ui/Feedback'
import { Modal } from '@/components/ui/Modal'
import { StatusPill } from '@/components/ui/StatusPill'
import { cn, formatDate, relativeTime } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { Partner, PartnerCoupon, PartnerDetail, UpdatePartnerRequest, UpdatePartnerResponse } from '@/types/api'

import { DailyChart } from './DailyChart'
import { PARTNER_STATUS, copyText, generatePassword, num, pct, usd } from './format'
import { CommissionsTab, CouponsTab, ReferralsTab, SettlementsTab } from './PartnerTabs'
import { FigureStrip, HeadlineCards, PasswordInput } from './ui'

type Tab = 'coupons' | 'users' | 'commissions' | 'settlements'
const TABS: Tab[] = ['coupons', 'users', 'commissions', 'settlements']

const partnerOf = (res: UpdatePartnerResponse): Partner => ('partner' in res && res.partner ? res.partner : (res as Partner))
const deactivatedOf = (res: UpdatePartnerResponse): number => num(res.deactivatedCoupons ?? res.couponsDeactivated ?? res.deactivated)

/**
 * One partner: the same four figures they see, day by day, then their codes,
 * the users those codes brought, each commission line and every settlement.
 */
export function PartnerDetailPage() {
  const { partnerId = '' } = useParams()
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()
  const tab = (TABS.find((t) => t === params.get('tab')) ?? 'coupons') as Tab
  const [confirmStatus, setConfirmStatus] = useState(false)
  const [resetting, setResetting] = useState(false)

  const detail = useQuery({ queryKey: ['partner', partnerId, 'detail'], queryFn: () => partnersApi.detail(partnerId) })

  const update = useMutation({
    mutationFn: (body: UpdatePartnerRequest) => partnersApi.update(partnerId, body),
    onSuccess: (res, body) => {
      const next = partnerOf(res)
      queryClient.setQueryData<PartnerDetail>(['partner', partnerId, 'detail'], (old) => (old && next?.id ? { ...old, partner: { ...old.partner, ...next } } : old))
      void queryClient.invalidateQueries({ queryKey: ['partner', partnerId] })
      void queryClient.invalidateQueries({ queryKey: ['partners'] })
      if (body.status) toast.success(body.status === 'suspended' ? 'Partner suspended' : 'Partner reactivated', body.status === 'suspended' ? 'They cannot sign in, and their codes stop working at checkout.' : 'They can sign in again.')
      if (body.maxDiscountPct !== undefined) {
        const n = deactivatedOf(res)
        toast.success(`Max discount is now ${pct(body.maxDiscountPct)}`, n ? `${n} ${n === 1 ? 'coupon was' : 'coupons were'} above it and deactivated.` : undefined)
      }
    },
    onError: (error) => toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  if (detail.isLoading) return <PageSpinner label="Loading the partner" />
  if (detail.isError || !detail.data) {
    const missing = detail.error instanceof ApiError && (detail.error.status === 404 || detail.error.status === 422)
    return (
      <div className="card">
        <ErrorState title={missing ? 'No such partner' : 'The partner could not be loaded'} onRetry={missing ? undefined : () => void detail.refetch()} />
      </div>
    )
  }

  const { partner, dashboard, coupons } = detail.data
  const t = dashboard.totals
  const status = PARTNER_STATUS[partner.status] ?? PARTNER_STATUS.active
  const suspended = partner.status === 'suspended'
  const openTab = (next: Tab) => setParams(next === 'coupons' ? {} : { tab: next }, { replace: true })
  const counts: Record<Tab, number | null> = { coupons: coupons.length, users: num(t.usersOnboarded), commissions: null, settlements: null }

  return (
    <>
      <Link to="/partners" className="mb-3 inline-flex h-9 items-center gap-1.5 rounded-field pl-1.5 pr-3 text-[13.5px] font-medium text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink">
        <BackIcon className="h-4 w-4" />
        Partners
      </Link>

      <header className="mb-5 flex flex-wrap items-start gap-x-4 gap-y-3">
        <Avatar name={partner.name} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h1 className="min-w-0 truncate text-[21px] font-semibold tracking-[-0.02em] text-ink sm:text-[25px]">{partner.name}</h1>
            <span className="num rounded-[6px] bg-accent-soft px-1.5 py-0.5 text-[12px] font-semibold text-ink-soft">{partner.tag}</span>
            <StatusPill tone={status.tone}>{status.label}</StatusPill>
          </div>
          <p className="mt-0.5 truncate text-[13.5px] text-ink-muted">
            {[partner.company, partner.email, partner.phone].filter(Boolean).join(' · ')}
          </p>
          <div className="mt-2.5">
            <MaxDiscount partner={partner} coupons={coupons} saving={update.isPending && update.variables?.maxDiscountPct !== undefined} onSave={(value) => update.mutateAsync({ maxDiscountPct: value })} />
          </div>
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          <Button variant="secondary" size="sm" onClick={() => setResetting(true)}>
            Reset password
          </Button>
          <Button variant={suspended ? 'success' : 'secondary'} size="sm" className={suspended ? undefined : 'text-danger hover:text-danger'} onClick={() => setConfirmStatus(true)}>
            {suspended ? 'Reactivate' : 'Suspend'}
          </Button>
        </div>
      </header>

      <HeadlineCards
        cards={[
          { key: 'pending', label: 'To be settled', value: usd(t.pendingCents), tone: num(t.pendingCents) > 0 ? 'warning' : 'neutral', hint: 'Earned, not paid out yet' },
          { key: 'earned', label: 'Overall earned', value: usd(t.earnedCents), hint: 'Pending + settled' },
          { key: 'users', label: 'Users onboarded', value: num(t.usersOnboarded).toLocaleString(), hint: `${num(t.couponsActive)} of ${num(t.couponsCreated)} codes live` },
          { key: 'settled', label: 'Already settled', value: usd(t.settledCents), tone: 'success', hint: 'Paid out to the partner' },
        ]}
      />

      <div className="mt-5 grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
        <section className="card min-w-0 p-5">
          <DailyChart daily={dashboard.daily ?? []} />
          <FigureStrip
            className="mt-5"
            figures={[
              { key: 'gross', label: 'List price', value: usd(t.grossCents), hint: 'Before the coupon' },
              { key: 'discount', label: 'Discount given', value: usd(t.discountCents) },
              { key: 'paid', label: 'Customers paid', value: usd(t.paidCents) },
              { key: 'invoices', label: 'Paid invoices', value: num(t.paidInvoices).toLocaleString() },
            ]}
          />
        </section>

        <aside className="panel divide-y divide-line">
          <p className="label-eyebrow px-4 py-3">Details</p>
          <Fact label="Email">{partner.email}</Fact>
          <Fact label="Phone">{partner.phone || '–'}</Fact>
          <Fact label="Joined">{formatDate(partner.createdAt)}</Fact>
          <Fact label="Last sign-in">{partner.lastLoginAt ? relativeTime(partner.lastLoginAt) : 'Never'}</Fact>
          <div className="px-4 py-2.5">
            <p className="text-[12.5px] text-ink-muted">Payout details</p>
            <p className="mt-1 whitespace-pre-wrap text-[13px] leading-relaxed text-ink-soft">{partner.payoutDetails || 'Not given yet. The partner adds these in their portal.'}</p>
          </div>
          {partner.notes && (
            <div className="px-4 py-2.5">
              <p className="text-[12.5px] text-ink-muted">Notes</p>
              <p className="mt-1 whitespace-pre-wrap text-[13px] leading-relaxed text-ink-soft">{partner.notes}</p>
            </div>
          )}
        </aside>
      </div>

      <div role="tablist" aria-label="Sections" className="hscroll mb-0 mt-6 gap-1 border-b border-line">
        {(
          [
            ['coupons', 'Coupons'],
            ['users', 'Users onboarded'],
            ['commissions', 'Commissions'],
            ['settlements', 'Settlements'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() => openTab(value)}
            className={cn(
              '-mb-px flex h-10 shrink-0 items-center gap-2 border-b-2 px-3 text-[14px] font-medium transition-colors',
              tab === value ? 'border-ink text-ink' : 'border-transparent text-ink-muted hover:text-ink',
            )}
          >
            {label}
            {counts[value] !== null && <span className="num text-[12px] text-ink-muted">{counts[value]}</span>}
          </button>
        ))}
      </div>

      <section className="card mt-4 overflow-hidden">
        {tab === 'coupons' && <CouponsTab coupons={coupons} maxDiscountPct={num(partner.maxDiscountPct)} />}
        {tab === 'users' && <ReferralsTab partnerId={partner.id} />}
        {tab === 'commissions' && <CommissionsTab partnerId={partner.id} />}
        {tab === 'settlements' && <SettlementsTab partnerId={partner.id} partnerName={partner.name} pendingCents={num(t.pendingCents)} />}
      </section>

      <Modal
        open={confirmStatus}
        onClose={() => !update.isPending && setConfirmStatus(false)}
        size="sm"
        title={suspended ? `Reactivate ${partner.name}?` : `Suspend ${partner.name}?`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmStatus(false)} disabled={update.isPending}>
              Cancel
            </Button>
            <Button
              variant={suspended ? 'success' : 'danger'}
              loading={update.isPending}
              onClick={() => update.mutate({ status: suspended ? 'active' : 'suspended' }, { onSettled: () => setConfirmStatus(false) })}
            >
              {suspended ? 'Reactivate' : 'Suspend'}
            </Button>
          </>
        }
      >
        <p className="pb-3 text-[13.5px] leading-relaxed text-ink-soft">
          {suspended
            ? 'They can sign in again and their active codes work at checkout again.'
            : 'They cannot sign in, and checkout refuses their codes. What they have earned stays, and you can still record settlements.'}
        </p>
      </Modal>

      <ResetPasswordModal open={resetting} onClose={() => setResetting(false)} partner={partner} />
    </>
  )
}

/** The maximum discount, edited in place; lowering it is confirmed with the coupons it will switch off. */
function MaxDiscount({ partner, coupons, saving, onSave }: { partner: Partner; coupons: PartnerCoupon[]; saving: boolean; onSave: (value: number) => Promise<unknown> }) {
  const current = num(partner.maxDiscountPct)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(String(current))
  const [confirming, setConfirming] = useState(false)
  const value = Number(draft)
  const valid = draft.trim() !== '' && Number.isFinite(value) && value >= 0 && value <= 100
  const affected = coupons.filter((c) => c.status === 'active' && num(c.discountPct) > value)

  const start = () => {
    setDraft(String(current))
    setEditing(true)
  }
  const save = async () => {
    try {
      await onSave(value)
      setConfirming(false)
      setEditing(false)
    } catch {
      setConfirming(false)
    }
  }

  if (!editing) {
    return (
      <p className="inline-flex flex-wrap items-center gap-2 rounded-field border border-line bg-surface px-3 py-1.5 text-[13px] text-ink-soft">
        Max discount <span className="num text-[15px] font-semibold text-ink">{pct(current)}</span>
        <button type="button" onClick={start} className="font-medium text-ink underline underline-offset-2 hover:text-brand">
          Edit
        </button>
      </p>
    )
  }

  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        if (valid && value !== current) setConfirming(true)
      }}
    >
      <label htmlFor="max-discount" className="text-[13px] text-ink-soft">
        Max discount
      </label>
      <div className="relative">
        <input
          id="max-discount"
          autoFocus
          type="number"
          inputMode="decimal"
          min={0}
          max={100}
          step={0.5}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => event.key === 'Escape' && setEditing(false)}
          className={cn('field-box num h-9 w-[96px] py-0 pr-7 text-[14px]', !valid && 'border-danger')}
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-ink-muted">%</span>
      </div>
      <Button type="submit" size="xs" disabled={!valid || value === current}>
        Save
      </Button>
      <Button size="xs" variant="ghost" onClick={() => setEditing(false)}>
        Cancel
      </Button>
      {!valid && <span className="text-[12.5px] text-danger">0 to 100</span>}

      <Modal
        open={confirming}
        onClose={() => !saving && setConfirming(false)}
        size="sm"
        title={`Change the max discount to ${pct(value)}?`}
        description={`From ${pct(current)}. Commissions already earned do not change.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant={affected.length ? 'danger' : 'primary'} loading={saving} onClick={() => void save()}>
              Change to {pct(value)}
            </Button>
          </>
        }
      >
        <div className="space-y-3 pb-3 text-[13.5px] leading-relaxed text-ink-soft">
          {value < current && (
            <p className="rounded-field bg-warning/[0.1] px-3 py-2 text-warning">Coupons above the new max will be deactivated.</p>
          )}
          {affected.length > 0 ? (
            <div>
              <p>
                {affected.length === 1 ? 'This active coupon is' : `These ${affected.length} active coupons are`} above {pct(value)}:
              </p>
              <ul className="mt-1.5 flex flex-wrap gap-1.5">
                {affected.map((c) => (
                  <li key={c.id} className="num rounded-pill bg-surface-muted px-2.5 py-1 text-[12px] font-medium text-ink">
                    {c.code} · {pct(c.discountPct)}
                  </li>
                ))}
              </ul>
            </div>
          ) : value < current ? (
            <p>No active coupon is above {pct(value)}, so none will be switched off.</p>
          ) : null}
          <p>Other codes earn the partner {pct(value)} minus the code's discount from now on.</p>
        </div>
      </Modal>
    </form>
  )
}

function ResetPasswordModal({ open, onClose, partner }: { open: boolean; onClose: () => void; partner: Partner }) {
  const [password, setPassword] = useState(() => generatePassword())
  const reset = useMutation({
    mutationFn: () => partnersApi.resetPassword(partner.id, password),
    onSuccess: async () => {
      const copied = await copyText(password)
      toast.success('Password reset', copied ? 'The new password is copied. Send it to the partner.' : 'Send the new password to the partner.')
      onClose()
      setPassword(generatePassword())
    },
    onError: (error) => toast.error('Could not reset the password', error instanceof ApiError ? error.message : undefined),
  })
  return (
    <Modal
      open={open}
      onClose={() => !reset.isPending && onClose()}
      size="sm"
      title="Reset the password"
      description={`For ${partner.name} (${partner.email}). Their current password stops working.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={reset.isPending}>
            Cancel
          </Button>
          <Button loading={reset.isPending} disabled={password.length < 12} onClick={() => reset.mutate()}>
            Reset password
          </Button>
        </>
      }
    >
      <div className="pb-3">
        <PasswordInput label="New password" value={password} onChange={setPassword} error={password && password.length < 12 ? 'At least 12 characters.' : null} hint="Copied when you reset it." />
      </div>
    </Modal>
  )
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-2.5">
      <span className="shrink-0 text-[12.5px] text-ink-muted">{label}</span>
      <span className="min-w-0 break-words text-right text-[13px] text-ink-soft">{children}</span>
    </div>
  )
}
